import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import PaginatedTable from '../PaginatedTable'
import DateTime from '../DateTime'
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  and,
  or
} from 'firebase/firestore'
import { db } from '../../firebase'
import Spinner from '../Spinner'
import { ApiContext, useApiRequest } from '../../hooks/api'

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : ''
}

const columns = [
  { name: 'ID', sortKey: 'id', render: (item) => item.id },
  { name: 'Name', sortKey: 'name', render: (item) => item.description },
  {
    name: 'Status',
    sortKey: 'status',
    render: (item) => capitalize(item.status)
  },
  { name: 'Organization', render: (item) => item.organization?.name || 'N/A' },
  {
    name: 'Timestamp',
    sortKey: 'createdAt',
    render: (item) => <DateTime value={item.createdAt} />
  }
]

export default function IncidentList() {
  const navigate = useNavigate()
  const { user } = useContext(ApiContext)
  const [profile] = useApiRequest(`users/${user?.id}`)

  const [allIncidentsData, setAllIncidentsData] = useState([])
  const [rows, setRows] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const [opts, setOpts] = useState({
    itemsPerPage: 10,
    persistent: true,
    uid: 'incidents-list'
  })

  const org_id = profile?.organization?.mapValue?.fields?.id?.integerValue

  // Preload all matching incidents upfront
  useEffect(() => {
    if (!org_id || !user?.id) return

    const preloadIncidents = async () => {
      setLoading(true)
      try {
        const incidentsCollection = collection(db, 'incidents')

        const fullQuery = query(
          incidentsCollection,
          or(
            where('dispatchOrganizationsIdList', 'array-contains', org_id),
            where('createdBy', '==', user?.id)
          ),
          orderBy('createdAt', 'desc')
        )

        const snapshot = await getDocs(fullQuery)
        const docs = snapshot.docs

        const incidentsData = docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        setTotalCount(incidentsData.length)
        setAllIncidentsData(incidentsData)

        const firstPageData = incidentsData.slice(0, opts.itemsPerPage)
        setRows(firstPageData)
      } catch (error) {
        console.error('Error preloading incidents:', error)
      } finally {
        setLoading(false)
      }
    }

    preloadIncidents()
  }, [org_id, user?.id, opts.itemsPerPage])

  // Handle pagination (random page selection)
  useEffect(() => {
    if (allIncidentsData.length === 0) return

    const startIndex = currentPage * opts.itemsPerPage
    const pageData = allIncidentsData.slice(
      startIndex,
      startIndex + opts.itemsPerPage
    )

    setRows(pageData)
  }, [currentPage, allIncidentsData, opts.itemsPerPage])

  const setPage = (page) => setCurrentPage(page)

  const setOptions = (newOpts) => {
    setOpts((prev) => ({ ...prev, ...newOpts }))
    setCurrentPage(0)
  }

  if (loading) return <Spinner />

  return (
    <div className='box'>
      <PaginatedTable
        className='is-fullwidth is-hoverable'
        columns={columns}
        rows={rows}
        currentPage={currentPage}
        totalCount={totalCount}
        loading={loading}
        setPage={setPage}
        opts={opts}
        setOpts={setOptions}
        onRowClick={(item) => navigate(`/incidents/${item.id}`)}
      />
    </div>
  )
}
